import { useEffect, useRef, useState } from 'react';
import MenuItem from '@/components/admin/MenuItem';
import { adminMenu, type MenuItemConfig } from '@/components/admin/menuConfig';
import authService from '@/services/authService';
import { useNavigate } from 'react-router-dom';
import { useSidebar } from '@/contexts/SidebarContext';
import { useAuth } from '@/contexts/AuthContext';
import { Icon } from '@iconify/react';
import { getArenaSlug } from '@/utils/arena';
import { getAdminBasePath } from '@/utils/adminPaths';
import { openExternalUrl } from '@/utils/openExternalUrl';
import fieldService from '@/services/fieldService';
import wilayahService from '@/services/wilayahService';

interface SidebarProps {
  menuItems?: MenuItemConfig[];
}

const Sidebar = ({ menuItems = adminMenu }: SidebarProps) => {
  const navigate = useNavigate();
  const { isCollapsed } = useSidebar();
  const { logout } = useAuth();
  const adminBase = getAdminBasePath();
  const [isDropupOpen, setIsDropupOpen] = useState(false);
  const dropupRef = useRef<HTMLDivElement>(null);
  const [arenaInfo, setArenaInfo] = useState<{ name: string; fieldCount: number; city: string }>({
    name: localStorage.getItem('arenaName') || 'Arena',
    fieldCount: 0,
    city: '',
  });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropupRef.current && !dropupRef.current.contains(event.target as Node)) {
        setIsDropupOpen(false);
      }
    };

    if (isDropupOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropupOpen]);

  const handleLogout = async () => {
    setIsDropupOpen(false);
    await logout();
    navigate('/');
  };

  const handleContactCS = () => {
    setIsDropupOpen(false);
    const textWhatsapp = `Halo Admin Sporto,\nSaya pengelola arena *${arenaInfo.name}* ingin menghubungi Customer Service Sporto untuk bantuan.\n\nTerima kasih.`;
    void openExternalUrl(`https://wa.me/6281252243582?text=${encodeURIComponent(textWhatsapp)}`);
  };

  useEffect(() => {
    let isMounted = true;
    const slug = authService.getSlug() || getArenaSlug();

    if (slug) {
      fieldService
        .getArenaDetail(slug)
        .then(async (data) => {
          if (!data || !isMounted) return;

          const fields = data.physical_fields || [];
          let cityName = '';

          // Resolve city from kode_wilayah -> core.wilayah
          if (data.kode_wilayah) {
            try {
              const wilayah = await wilayahService.getWilayahByKode(data.kode_wilayah);
              if (wilayah?.nama) {
                cityName = wilayah.nama.replace(/^(Kabupaten|Kota)\s+/i, '');
              } else if (wilayah?.namaLengkap) {
                cityName = wilayah.namaLengkap;
              }
            } catch (err) {
              console.error('Failed to resolve wilayah by kode:', err);
            }
          }

          // Fallback to maps.city or address if kode_wilayah is not resolved
          if (!cityName) {
            cityName = data.maps?.city || data.address || '';
          }

          if (isMounted) {
            setArenaInfo({
              name: data.name || localStorage.getItem('arenaName') || 'Arena',
              fieldCount: fields.length,
              city: cityName,
            });
          }
        })
        .catch(() => {});
    }

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <div className={`h-full max-h-screen flex flex-col justify-between overflow-hidden select-none ${isCollapsed ? 'p-3 items-center' : 'p-5'}`}>
      <div className="space-y-6 flex-1 min-h-0 flex flex-col overflow-hidden">
        {/* Header: Logo + Arena Badge */}
        <div
          className={`flex items-center gap-2 cursor-pointer shrink-0 ${isCollapsed ? 'justify-center w-full' : ''}`}
          onClick={() => navigate('/')}
          title="Sporto"
        >
          {isCollapsed ? (
            <img
              src="https://cdn.sporto.id/sporto-small.svg"
              alt="sporto"
              className="w-8 h-8 object-contain transition-transform duration-200 hover:scale-105"
            />
          ) : (
            <div className="flex items-center gap-2 justify-between w-full">
              <img
                src="https://cdn.sporto.id/sporto-black.png"
                alt="sporto"
                className="h-8 w-auto transition-opacity duration-200"
              />
              <span className="bg-[#FFF5EE] text-[#FC480D] border border-orange-200/80 font-semibold text-[11px] px-2.5 py-0.5 rounded-full">
                Arena
              </span>
            </div>
          )}
        </div>

        {/* Navigation Menu */}
        <nav className={`space-y-1 w-full overflow-y-auto scrollbar-none flex-1 min-h-0 ${isCollapsed ? 'flex flex-col items-center' : ''}`}>
          {menuItems.map((item) => {
            if (item.roles && !item.roles.includes(authService.getRole() as string)) {
              return null;
            }
            return (
              <MenuItem
                key={item.path}
                label={item.label}
                icon={item.icon}
                to={item.path}
                collapsed={isCollapsed}
              />
            );
          })}
        </nav>
      </div>

      {/* Bottom: Arena Info Card & Profile Dropup */}
      {!isCollapsed ? (
        <div ref={dropupRef} className="pt-3 mt-auto border-t border-gray-100 shrink-0 relative">
          {/* Dropup Menu Card */}
          {isDropupOpen && (
            <div className="absolute bottom-full left-0 right-0 mb-2 bg-white rounded-2xl border border-gray-200/80 shadow-xl p-2 z-50 space-y-0.5 animate-in fade-in slide-in-from-bottom-2 duration-150">
              <button
                type="button"
                onClick={() => {
                  navigate(`${adminBase}/profile-settings`);
                  setIsDropupOpen(false);
                }}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-semibold text-[#0E1726] hover:bg-gray-50 transition-colors text-left cursor-pointer"
              >
                <Icon icon="ph:user" className="w-4 h-4 text-gray-500 shrink-0" />
                <span>Profil</span>
              </button>
              
              <button
                type="button"
                onClick={() => {
                  setIsDropupOpen(false);
                  handleContactCS();
                }}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-semibold text-[#0E1726] hover:bg-gray-50 transition-colors text-left cursor-pointer"
              >
                <Icon icon="ph:headset" className="w-4 h-4 text-gray-500 shrink-0" />
                <span>Hubungi CS</span>
              </button>

              <div className="my-1 border-t border-gray-100" />

              <button
                type="button"
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-semibold text-[#E53935] hover:bg-red-50/50 transition-colors text-left cursor-pointer"
              >
                <Icon icon="ph:sign-out" className="w-4 h-4 text-[#E53935] shrink-0" />
                <span>Logout</span>
              </button>
            </div>
          )}

          {/* Arena Info Card Button */}
          <button
            type="button"
            onClick={() => setIsDropupOpen((prev) => !prev)}
            className={`w-full p-2.5 sm:p-3 bg-white rounded-2xl border transition-all flex items-center gap-2.5 text-left cursor-pointer shadow-2xs ${
              isDropupOpen
                ? 'border-[#FC480D] ring-2 ring-orange-100'
                : 'border-gray-200/80 hover:border-gray-300'
            }`}
          >
            <div className="w-9 h-9 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-center shrink-0 text-gray-500">
              <Icon icon="ph:buildings-bold" className="w-4 h-4 text-gray-500" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-xs font-semibold text-[#0E1726] truncate">
                {arenaInfo.name}
              </div>
              <div className="text-[11px] font-medium text-gray-400 truncate">
                {arenaInfo.city ? arenaInfo.city : arenaInfo.fieldCount > 0 ? `${arenaInfo.fieldCount} lapangan` : 'Venue Partner'}
              </div>
            </div>
            <Icon
              icon={isDropupOpen ? 'ph:caret-up-bold' : 'ph:caret-down-bold'}
              className={`w-3.5 h-3.5 shrink-0 transition-colors ${
                isDropupOpen ? 'text-[#FC480D]' : 'text-gray-400'
              }`}
            />
          </button>
        </div>
      ) : (
        <div ref={dropupRef} className="pt-3 mt-auto border-t border-gray-100 flex justify-center w-full shrink-0 relative">
          {/* Dropup for Collapsed Sidebar */}
          {isDropupOpen && (
            <div className="absolute bottom-0 left-full ml-2 w-48 bg-white rounded-2xl border border-gray-200/80 shadow-xl p-2 z-50 space-y-0.5 animate-in fade-in slide-in-from-left-2 duration-150">
              <div className="px-3 py-2 border-b border-gray-100 mb-1">
                <div className="text-xs font-semibold text-[#0E1726] truncate">{arenaInfo.name}</div>
                <div className="text-[10px] text-gray-400 truncate">{arenaInfo.city}</div>
              </div>

              <button
                type="button"
                onClick={() => {
                  navigate(`${adminBase}/profile-settings`);
                  setIsDropupOpen(false);
                }}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-semibold text-[#0E1726] hover:bg-gray-50 transition-colors text-left cursor-pointer"
              >
                <Icon icon="ph:user" className="w-4 h-4 text-gray-500 shrink-0" />
                <span>Profil</span>
              </button>

              <button
                type="button"
                onClick={() => {
                  setIsDropupOpen(false);
                  handleContactCS();
                }}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-semibold text-[#0E1726] hover:bg-gray-50 transition-colors text-left cursor-pointer"
              >
                <Icon icon="ph:headset" className="w-4 h-4 text-gray-500 shrink-0" />
                <span>Hubungi CS</span>
              </button>

              <div className="my-1 border-t border-gray-100" />

              <button
                type="button"
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-semibold text-[#E53935] hover:bg-red-50/50 transition-colors text-left cursor-pointer"
              >
                <Icon icon="ph:sign-out" className="w-4 h-4 text-[#E53935] shrink-0" />
                <span>Logout</span>
              </button>
            </div>
          )}

          <button
            type="button"
            onClick={() => setIsDropupOpen((prev) => !prev)}
            className={`w-10 h-10 rounded-xl bg-gray-50 border flex items-center justify-center text-gray-500 hover:text-gray-800 transition-all cursor-pointer ${
              isDropupOpen ? 'border-[#FC480D] ring-2 ring-orange-100 text-[#FC480D]' : 'border-gray-100 hover:border-gray-300'
            }`}
            title={arenaInfo.name}
          >
            <Icon icon="ph:buildings-bold" className="w-5 h-5" />
          </button>
        </div>
      )}
    </div>
  );
};

export default Sidebar;
